// Settings — the shared type vocabulary for the account/preferences surfaces.
//
// This file is the single place the settings components agree on their shapes,
// so every panel reads the same named types (typed all the way up — no raw
// object literals cross a public surface). Nothing here is a meaning-swapping
// boolean: a section's identity is a named union, and the "current values"
// objects are genuine multi-field snapshots (allowed), not flags.
//
// APPLY-ON-CHANGE is the model these types encode. Each panel is a PURE VIEW
// of a current-values snapshot; every edit is reported UP through a per-field
// callback. There is deliberately no `onSave(values)` and no dirty state:
//   - theme + language are per-device preferences that must take effect the
//     instant they change (theme lives outside the event log by design — see
//     memory project_theme_outside_event_log), so a Save gate would be wrong;
//   - a text field like the display name still reports every change up, and
//     the CONSUMER owns when to commit (e.g. on blur) — the component stays
//     pure and holds no policy.
// One coherent model, less chrome, no unsaved-changes anxiety.
//
// ACCOUNT vs IAM boundary (owned here = Account):
//   Account  = "my profile + my preferences for myself" (this folder).
//   IAM      = "managing OTHER users / tenants / roles" (components/Iam/,
//              a separate surface). WorkspaceRole is DISPLAYED read-only here
//              (my membership); assigning roles to other people is IAM's job.

import type { ReactNode } from "react";
import type { DesktopPlatform } from "../../app/desktopBridge.js";
import type { Locale } from "../../i18n/supportedLocales.js";

// The identity of one settings section — a named union, discriminated by the
// literal itself (the SegmentedOption/ButtonVariant precedent). Drives the
// section nav and selects the active panel. Each section is a distinct
// DESTINATION, not a flag toggled on one screen. `assistant` and `data` are
// the desktop-first sections: their panels render live controls only when the
// optional DesktopBridge is present, and an honest web-build note otherwise.
export type SettingsSectionKind =
    | "account"
    | "preferences"
    | "assistant"
    | "data"
    // IAM: who you are + who else is in this workspace — the sign-in
    // identity, the workspace switcher, members/invite, the one
    // delete-account-and-workspace action, and (folded in here, since there
    // is nothing else in "workspace operations" today) exporting the event
    // log. Distinct from "account" above (a per-device display-name
    // preference that holds regardless of sign-in). Reachable when
    // authStatus is "authenticated" (the full IAM host) OR "unavailable"/
    // fs-mode (the honest read-only local-workspace view — one computer, no
    // members, no switcher: see docs/model/bcs/access.md's "fs-mode
    // (today)" deployment row).
    | "workspace"
    // The app's name and version (moved from the menu's foot).
    | "about";

// The section kinds as a runtime list — the closed set a URL/route parser
// validates a `/settings/<section>` segment against (F48 URL navigation). A
// `satisfies` (not a cast) keeps it exhaustive: a new section kind that isn't
// added here fails the type-check.
export const SETTINGS_SECTIONS = [
    "account",
    "preferences",
    "assistant",
    "data",
    "workspace",
    "about",
] as const satisfies readonly SettingsSectionKind[];

// One entry in the section nav: its identity plus the label/glyph the nav
// renders. The label is caller-owned (localised) ReactNode, never a baked
// string.
export type SettingsNavItem = {
    readonly kind: SettingsSectionKind;
    readonly label: ReactNode;
    readonly icon?: ReactNode;
};

// === Preferences ===

// The colour theme. "system" defers to the OS; light/dark are explicit. The
// TYPE keeps all three positions as the seam for when OS-inference actually
// ships, but the Preferences UI offers only Light/Dark today — "system" and
// "light" render pixel-identical (`app/theme.tsx`'s `effectiveTheme`), so
// exposing a third control position with no observable effect would be a
// decision-in-disguise (.claude/analysis/settings-simplification-2026-09-22.md
// §12). A device that already persisted "system" keeps reading as light —
// no migration, same pixels either way.
export type ThemePreference = "system" | "light" | "dark";

// The UI language: one of the supported locales (i18n/supportedLocales.ts,
// the single table — a new locale there flows here with no second edit).
export type LanguagePreference = Locale;

// The current preference values — a genuine multi-field snapshot (this is the
// sanctioned "these are the values" object, NOT a meaning-swap). The panel
// renders it; each field changes via its own callback.
export type PreferenceValues = {
    readonly theme: ThemePreference;
    readonly language: LanguagePreference;
};

// === Account ===

// The person's own identity. The world is always authenticated (Stream B
// removed the local no-auth world), so the identity IS your sign-in email —
// the account panel shows real identity, never a device display-name standing
// in for it. The display name stays a per-device PREFERENCE (persisted like
// the theme, outside the event log — app/displayName.ts), shown on the
// materials you author; it is not your identity. `pending` covers the
// sub-second window before the `/auth/me` probe resolves: the panel shows the
// display name and a quiet "signing in" note, never a faked email.
// Summands are declared but NOT exported (the union is the public surface):
// consumers narrow on the `type` discriminant, never import an arm by name.
type AuthenticatedAccount = {
    readonly type: "authenticated";
    readonly email: string;
    readonly displayName: string;
};
type PendingAccount = {
    readonly type: "pending";
    readonly displayName: string;
};
export type AccountIdentity = AuthenticatedAccount | PendingAccount;

// === Assistant (desktop BYOK API key) ===

// What the panel knows about the agent API key — a named union of positions,
// not booleans (a flag pair `webBuild` + `configured` would smuggle two
// meaning-swaps into one shape). CREDENTIAL DISCIPLINE: this is PRESENCE only;
// the key value never reaches the renderer (see app/desktopBridge.ts).
//   web_build      — no DesktopBridge: the honest "desktop app only" note.
//   checking       — bridge present, presence read still in flight.
//   configured     — a key is stored on this computer (validity unknown).
//   not_configured — no key stored yet.
export type AssistantKeyState =
    | "web_build"
    | "checking"
    | "configured"
    | "not_configured";

// === Data (desktop local-first events file) ===

// Where the local events file is, as the DataPanel sees it. A discriminated
// sum (one named type per variant) so the panel matches totally — no partial
// fields, no "loaded but path missing" limbo. `sizeLabel` is PRE-FORMATTED by
// the consumer (the panel never formats bytes — presentation data arrives
// ready, keeping the panel a pure view). `platform` picks the reveal-action
// register (Finder / Explorer / file manager).
export type DataLocationWebBuild = { readonly type: "web_build" };
export type DataLocationLoading = { readonly type: "loading" };
export type DataLocationLoaded = {
    readonly type: "loaded";
    readonly eventsPath: string;
    readonly sizeLabel: string;
    readonly platform: DesktopPlatform;
};
export type DataLocationState =
    | DataLocationWebBuild
    | DataLocationLoading
    | DataLocationLoaded;
